'use client'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const MarkdownContent = ({ content = '', className = '', size = 'sm' }) => {
  if (typeof content !== 'string' || content.trim() === '') return null;

  const baseText = size === 'xs' ? 'text-xs' : 'text-sm';
  const bodyText = size === 'xs' ? 'text-xs' : 'text-[13px] sm:text-sm';

  const components = {
    h1: ({ children }) => (
      <h1 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight mt-5 mb-3 flex items-start gap-2.5">
        <span className="inline-block w-1.5 h-[1.1em] rounded-full bg-primary shrink-0 mt-0.5" />
        <span className="break-words min-w-0">{children}</span>
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-[15px] sm:text-base font-bold text-gray-900 mt-5 mb-2.5 pb-2 border-b border-gray-100 flex items-center gap-2">
        <span className="inline-block w-5 h-[3px] rounded-full bg-primary shrink-0" />
        <span className="break-words min-w-0">{children}</span>
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-sm sm:text-[15px] font-bold text-gray-900 mt-4 mb-1.5 break-words">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-sm font-semibold text-gray-900 mt-3 mb-1 break-words">{children}</h4>
    ),
    h5: ({ children }) => (
      <h5 className="text-[13px] font-semibold text-gray-800 mt-3 mb-1 break-words">{children}</h5>
    ),
    h6: ({ children }) => (
      <h6 className="text-xs font-semibold text-gray-700 mt-2 mb-1 break-words">{children}</h6>
    ),
    p: ({ children }) => (
      <p className={`${bodyText} text-gray-700 leading-relaxed my-2 break-words last:mb-0`}>{children}</p>
    ),
    strong: ({ children }) => (
      <strong className="font-bold text-gray-900">{children}</strong>
    ),
    em: ({ children }) => (
      <em className="italic text-gray-700">{children}</em>
    ),
    del: ({ children }) => (
      <del className="line-through text-gray-400">{children}</del>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary font-medium underline underline-offset-2 hover:opacity-80 transition-opacity break-all"
      >
        {children}
      </a>
    ),
    ul: ({ children }) => (
      <ul className="list-disc pl-5 my-2 space-y-1 marker:text-primary">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal pl-5 my-2 space-y-1 marker:text-primary">{children}</ol>
    ),
    li: ({ children }) => (
      <li className={`${bodyText} text-gray-700 leading-relaxed pl-1 break-words`}>{children}</li>
    ),
    hr: () => (
      <hr className="my-4 h-px border-0 bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-primary bg-primary-bg-lighter rounded-r-lg py-1.5 pl-3.5 pr-3 my-3">
        {children}
      </blockquote>
    ),
    table: ({ children }) => (
      <div className="my-3 overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-xs sm:text-[13px] text-left border-collapse">{children}</table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-gray-100 text-gray-800 font-semibold">{children}</thead>
    ),
    th: ({ children }) => (
      <th className="px-3 py-2 border-b border-gray-200 whitespace-nowrap font-semibold">{children}</th>
    ),
    td: ({ children }) => (
      <td className="px-3 py-2 border-b border-gray-100 align-top break-words">{children}</td>
    ),
    code: ({ className, children }) => {
      const isBlock = className && className.includes('language-');
      if (isBlock) {
        return <code className="block font-mono text-xs leading-relaxed text-gray-100">{children}</code>;
      }
      return (
        <code className="bg-gray-100 border border-gray-200 rounded-md px-1.5 py-0.5 font-mono text-[0.85em] text-gray-800 break-all whitespace-pre-wrap">
          {children}
        </code>
      );
    },
    pre: ({ children }) => {
      const inner = children?.props?.children ?? children;
      return (
        <pre className="my-3 rounded-lg bg-gray-900 p-3.5 overflow-x-auto text-xs font-mono text-gray-100 leading-relaxed whitespace-pre-wrap break-words">
          {inner}
        </pre>
      );
    },
    img: ({ src, alt }) => (
      <img
        src={src}
        alt={alt || ''}
        className="my-3 max-w-full h-auto rounded-lg shadow-sm"
        loading="lazy"
      />
    ),
    input: ({ checked, type }) =>
      type === 'checkbox' ? (
        <input type="checkbox" checked={checked} disabled className="mr-2 accent-primary align-middle" />
      ) : null,
  };

  return (
    <div className={`${baseText} text-gray-700 markdown-content ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
